/** @jsxImportSource @emotion/react */
import styled from "@emotion/styled";
import { ServerEnv } from "Configs/ServerEnv";
import { useEffect, useRef, useCallback } from "react";

function ApplePayButton({
    handleSubmit,
    amount,
    currency,
    tax,
    shipping,
    market,
    products,
    disabled = false
}) {
    const applePayRef = useRef(null);
    const sessionActive = useRef(false);

    const APPLE_PAY_VERSION = 14;

    console.log('window.canMakePayments', window.ApplePaySession.canMakePayments())

    const getDecimalPlaces = (currencyCode) => {
        const threeDecimal = ['KWD', 'BHD', 'OMR']
        const zeroDecimal = ['JPY', 'KRW', 'VND']
        
        if (threeDecimal.includes(currencyCode)) return 3
        if (zeroDecimal.includes(currencyCode)) return 0
        return 2
    }

    const getDemical = getDecimalPlaces(currency)

    const fakeApplePayResponse = () => {
        return {
            token: {
                paymentData: {
                    data: "FAKE_APPLE_PAY_DATA",
                    signature: "FAKE_SIGNATURE",
                    header: {
                        ephemeralPublicKey: "FAKE_EPHEMERAL_KEY",
                        publicKeyHash: "FAKE_PUBLIC_KEY_HASH",
                        transactionId: `FAKE_TX_${Date.now()}`
                    },
                    version: "EC_v1"
                },
                paymentMethod: {
                    displayName: "Visa 4242",
                    network: "Visa",
                    type: "credit"
                },
                transactionIdentifier: `FAKE_TX_${Date.now()}`
            }
        }
    }


    const handleButtonClick = useCallback(() => {
        // if (IS_PROD === 'DEV') {
        //     console.warn("⚠️ Apple Pay MOCK MODE");
        
        //     const fakeResult = fakeApplePayResponse()
        
        //     handleSubmit(fakeResult);
        //     return
        // }

        if (disabled) {
            console.log("Apple Pay button is disabled");
            return;
        }

        if (!window.ApplePaySession || !window.ApplePaySession.canMakePayments()) {
            console.log("Apple pay not available");
            return;
        }

        if (sessionActive.current) {
            console.warn("Apple Pay session is already active.");
            return;
        }

        const paymentRequest = {
            countryCode: market || "US",
            currencyCode: currency || "USD",
            merchantCapabilities: ["supports3DS"],
            supportedNetworks: ["visa", "masterCard", "amex"],
            requiredBillingContactFields: [
                "name",
                "email",
                "phone",
                "postalAddress"
            ],
            total: {
                label: "Unicity",
                type: "final",
                amount: Number(amount).toFixed(getDemical)
            }
        };

        const session = new window.ApplePaySession(
            APPLE_PAY_VERSION,
            paymentRequest
        );

        sessionActive.current = true;

        session.onvalidatemerchant = async () => {
            const shopURL = 'fg.unicity.com'
            
            const response = await fetch(
                `${ServerEnv.Hydra()}/applepay/getSession?shopURL=${shopURL}`,
                { method: "POST" }
            );

            console.log('response onvalidatemerchant', response)

            if (!response.ok) {
                throw new Error("Network response was not ok");
            }

            const merchantSession = await response.json();

            session.completeMerchantValidation(merchantSession);
        };

        session.onpaymentmethodselected = () => {
            const newTotal = {
                label: "Unicity",
                amount: Number(amount).toFixed(getDemical),
                type: "final",
                currency: currency || "USD"
            };

            const productsData = products ? products.map(product => {
                return {
                    label: product.item_name,
                    amount: Number(product.price).toFixed(getDemical),
                    type: "final",
                    currency: currency || "USD"
                }
            }) : []

            const newLineItems = [
                ...productsData,
                {
                    label: "Tax",
                    amount: Number(tax).toFixed(getDemical),
                    type: "final",
                    currency: currency || "USD"
                },
                {
                    label: "Shipping",
                    amount: Number(shipping).toFixed(getDemical),
                    type: "final",
                    currency: currency || "USD"
                }
            ];

            try {
                session.completePaymentMethodSelection(newTotal, newLineItems);
            } catch (error) {
                console.error(
                    "Error completing payment method selection:",
                    error
                );
            }
        };

        session.onpaymentauthorized = async (event) => {
            const applePayResult = event.payment;

            handleSubmit(applePayResult);
            session.completePayment(window.ApplePaySession.STATUS_SUCCESS);
        };

        session.oncancel = () => {
            sessionActive.current = false;
        };

        session.begin();
    }, [handleSubmit, amount, currency, market, products, shipping, tax, disabled]);

    useEffect(() => {
        const applePayButton = applePayRef.current;

        if (applePayButton) {
            applePayButton.addEventListener("click", handleButtonClick);
        }

        return () => {
            if (applePayButton) {
                applePayButton.removeEventListener("click", handleButtonClick);
            }
        };
    }, [handleButtonClick]);

    if (typeof window === "undefined" || !window.ApplePaySession) {
        return null;
    }

    return (
        <ApplePayButtonStyled
            borderRadius={40}
            buttonstyle="black"
            type="buy"
            id="apple-pay-button"
            locale="en-US"
            ref={applePayRef}
            disabled={disabled}
        />
    );
}

const ApplePayButtonStyled = styled.button`
  appearance: none;
  -webkit-appearance: -apple-pay-button;
  -apple-pay-button-type: buy;
  -apple-pay-button-style: black;

  width: 100%;
  height: 48px;
  margin-top: 25px;
  border-radius: 34px;

  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.5 : 1};
  pointer-events: ${props => props.disabled ? 'none' : 'auto'};

  @media screen and (min-width: 700px) {
    width: 340px;
  }
`;

export default ApplePayButton;
